/**
 * ContactDetail (conversation view)
 *
 * The single unified communication window per person, conversation-first: a
 * slim identity header (name, phone/email/lead-since, study chips, call +
 * lifecycle + owner controls), then a fixed-height conversation card —
 * CommsThread as the scrollable chat region with the Composer docked at the
 * bottom — beside the follow-up tasks, screener answers and humanized
 * automation enrollments (+ super-admin test re-enroll). Reached at
 * /crm/contacts/:id. Data via the
 * authed backend API; live via CommsThread's Realtime broadcast + fallback
 * poll and a reloadToken bump on send.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, ChevronLeft, Phone, Mail, Ban, MessageSquare, CalendarDays, Archive, ArchiveRestore, BookOpen, ListOrdered, Pencil, History, FlaskConical } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { ContactDetailResponse, Enrollment, Contact, ScheduleReadiness, ScheduleReadinessItem, contactName, studyInternalLabel } from '../types';
import { attemptStatusMeta, FINAL_DISPOSITIONS, formatPhone, getLeadOrigin, leadOriginLabel, notifyStatsChanged, prettySlug, useTeam } from '../lib';
import { CommsThread, Composer, ScheduledMessagesBar, CallButton, LifecycleSelect, OwnerSelect, PrimaryStudySelect } from '../components';
import ContactRail from '../components/ContactRail';
import EditContactModal from '../components/EditContactModal';
import PushToCtmsButton from '../components/PushToCtmsButton';
import StudyReferenceDrawer from '../components/studies/StudyReferenceDrawer';

/** Human copy for a compliance-gate block (same set the composer surfaces). */
function blockedReason(reason?: string): string {
  switch (reason) {
    case 'opted_out': return 'contact has opted out of SMS';
    case 'consent_revoked': return 'SMS consent was revoked';
    case 'quiet_hours': return 'outside allowed messaging hours';
    case 'no_phone': return 'contact has no phone number';
    case 'no_comprehensive_form': return 'no published comprehensive form — publish one under Forms first';
    default: return reason || 'blocked';
  }
}

export default function ContactDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { byId: usersById } = useTeam();
  const isSuperAdmin = user?.role === 'super_admin';

  const [detail, setDetail] = useState<ContactDetailResponse | null>(null);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  // Bumps the ScheduledMessagesBar (new schedule + fired-send ping) WITHOUT
  // forcing CommsThread's scroll-to-bottom the way reloadToken would.
  const [schedToken, setSchedToken] = useState(0);
  const [reenrolling, setReenrolling] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [archBusy, setArchBusy] = useState(false);
  const [archError, setArchError] = useState<string | null>(null);
  const [sendingOptions, setSendingOptions] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [refOpen, setRefOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  // Scheduling readiness for the lead's current study — powers the rail checklist
  // and the header's "requirements not met" warning on the lifecycle changer.
  const [scheduleReadiness, setScheduleReadiness] = useState<ScheduleReadiness | null>(null);
  // One-shot draft pushed from a rail quick action (booking link) into the
  // docked Composer; cleared the moment the Composer consumes it.
  const [composerDraft, setComposerDraft] = useState<{ text: string; mode?: 'sms' } | null>(null);
  // Debounced (~1s) Realtime ping for rail cards (RecordsCard): backend state
  // changes already emit the broadcast; bumping reloadToken instead would
  // force CommsThread's scroll-to-bottom on every background ping.
  // Back target: the CRM page this lead was opened from (recorded by
  // CRMRoutes), falling back to the Leads inbox on a deep link / fresh tab.
  const backTo = getLeadOrigin() || '/crm/inbox';
  const backLabel = leadOriginLabel(backTo);
  const [pingTick, setPingTick] = useState(0);
  const pingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onPing = useCallback(() => {
    if (pingTimer.current) return;
    pingTimer.current = setTimeout(() => {
      pingTimer.current = null;
      setPingTick((t) => t + 1);
    }, 1000);
  }, []);
  useEffect(() => () => {
    if (pingTimer.current) clearTimeout(pingTimer.current);
  }, []);

  const loadEnrollments = useCallback(async () => {
    try {
      const enr = await api.get(`/api/crm/contacts/${id}/enrollments`);
      setEnrollments(enr.data?.enrollments || []);
    } catch {
      setEnrollments([]);
    }
  }, [id]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [res] = await Promise.all([api.get(`/api/crm/contacts/${id}`), loadEnrollments()]);
      setDetail(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [id, loadEnrollments]);

  useEffect(() => { load(); }, [load]);

  // Silent contact refetch when the timeline changes (reloadToken bumps after
  // every send): the DB trigger has already moved the worklist columns
  // (attempt_count / needs_reply_at / next_action_at) by the time the send API
  // responds, and CallbacksCard's cadence line reads them from this response.
  // No setLoading — a full-page spinner on every send would be jarring.
  useEffect(() => {
    if (reloadToken === 0) return;
    let stale = false;
    api.get(`/api/crm/contacts/${id}`)
      .then((res) => { if (!stale) setDetail(res.data); })
      .catch(() => { /* next full load or send retries */ });
    return () => { stale = true; };
  }, [reloadToken, id]);

  // Scheduling readiness — refetched when the lead's study changes, on send/ping,
  // and after a stage change (an auto gate like "expresses interest" can flip).
  const primaryStudyId = detail?.primary_study?.id ?? detail?.contact.primary_study_id ?? null;
  const contactLifecycle = detail?.contact.lifecycle_status ?? null;
  const loadReadiness = useCallback(async () => {
    if (!primaryStudyId) { setScheduleReadiness(null); return; }
    try {
      const res = await api.get(`/api/crm/contacts/${id}/schedule-gates`, { params: { study_id: primaryStudyId } });
      setScheduleReadiness(res.data);
    } catch {
      setScheduleReadiness(null);
    }
  }, [id, primaryStudyId]);
  useEffect(() => { loadReadiness(); }, [loadReadiness, reloadToken, pingTick, contactLifecycle]);

  // Tick a manual scheduling gate (CRC / investigator / custom sign-off). The
  // POST returns the recomputed readiness so the header warning updates too.
  const onToggleGate = useCallback(async (item: ScheduleReadinessItem, done: boolean) => {
    const studyId = scheduleReadiness?.study_id;
    if (!studyId) return;
    try {
      const res = await api.post(`/api/crm/contacts/${id}/schedule-gates`, {
        study_id: studyId, gate_key: item.key, done,
      });
      if (res.data?.readiness) setScheduleReadiness(res.data.readiness);
      else await loadReadiness();
    } catch {
      await loadReadiness();
    }
  }, [id, scheduleReadiness?.study_id, loadReadiness]);

  const reEnroll = async () => {
    setReenrolling(true);
    setMsg(null);
    try {
      const res = await api.post(`/api/crm/contacts/${id}/re-enroll`);
      const d = res.data || {};
      const enrolledCount = (d.results || []).filter((r: any) => r.enrolled).length;
      const tick = d.tick || {};
      await loadEnrollments();
      setReloadToken((t) => t + 1);
      setMsg(`Re-enrolled into ${enrolledCount} flow(s); tick claimed ${tick.claimed ?? 0}, SMS enqueued ${tick.enqueuedSms ?? 0}. The welcome sends shortly.`);
    } catch (e: any) {
      const reason = e?.response?.data?.reason;
      setMsg(`Re-enroll failed: ${reason ? `refused (${reason})` : (e?.response?.data?.detail || e?.response?.data?.error || e.message)}`);
    } finally {
      setReenrolling(false);
    }
  };

  /** Text the contact the three ways forward (book / form / not interested) —
   *  Phase D respond-options quick action; same gate + 409/422 shapes as the
   *  booking-link and form-link sends. */
  const sendOptions = async () => {
    if (sendingOptions) return;
    setSendingOptions(true);
    setOptionsError(null);
    setMsg(null);
    try {
      await api.post(`/api/crm/contacts/${id}/respond-options`, { send: true });
      setMsg('Options message texted — they can reply 1 (book a call), 2 (fill the form) or 3 (not interested).');
      notifyStatsChanged(); // the send counts as an outreach attempt
      setReloadToken((t) => t + 1);
    } catch (e: any) {
      const d = e?.response?.data || {};
      if (e?.response?.status === 409 && d.reason) {
        setOptionsError(`Options message not sent: ${blockedReason(d.reason)}.`);
      } else {
        const base = d.detail || d.error || e.message || 'Send failed';
        setOptionsError(d.hint ? `${base} — ${d.hint}` : base);
      }
    } finally {
      setSendingOptions(false);
    }
  };

  /** Archive/unarchive this conversation (optimistic; reverts on failure). */
  const toggleArchived = async () => {
    if (!detail || archBusy) return;
    const prevArchivedAt = detail.contact.archived_at ?? null;
    const nextArchived = !prevArchivedAt;
    setArchBusy(true);
    setArchError(null);
    setDetail((d) =>
      d ? { ...d, contact: { ...d.contact, archived_at: nextArchived ? new Date().toISOString() : null } } : d
    );
    try {
      await api.patch(`/api/crm/contacts/${id}`, { archived: nextArchived });
      notifyStatsChanged();
    } catch (e: any) {
      setDetail((d) => (d ? { ...d, contact: { ...d.contact, archived_at: prevArchivedAt } } : d));
      setArchError(e?.response?.data?.detail || e?.response?.data?.error || e.message || 'Could not update the conversation.');
    } finally {
      setArchBusy(false);
    }
  };

  // Lead-linked study slugs (the reference drawer's default ordering).
  const leadStudySlugs = useMemo(
    () => [...new Set((detail?.leads || []).map((l) => l.study_slug).filter(Boolean))] as string[],
    [detail?.leads]
  );

  // "Came in for" — ONLY the most recent landing-page submission (the latest
  // form they completed to re-enter the system), not the full history. Screener
  // submissions arrive most-recent-first (buildScreener sorts by first_seen_at
  // DESC), so the first one with a resolvable study is the current entry reason.
  const cameInFor = useMemo(() => {
    if (!detail) return null as { label: string; source: string | null } | null;
    const sourceByLeadId = new Map<string, string | null>();
    for (const l of detail.leads || []) sourceByLeadId.set(l.lead_id, l.lead_source ?? null);
    for (const s of detail.screener?.submissions || []) {
      const label = s.study_internal_title || s.study_title || prettySlug(s.study_slug);
      if (!label || label === '—') continue;
      const src = s.lead_id ? sourceByLeadId.get(s.lead_id) || null : null;
      return { label, source: src ? prettySlug(src) : null };
    }
    return null;
  }, [detail]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate(backTo)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 transition-colors duration-150 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
        >
          <ChevronLeft className="h-4 w-4" /> {backLabel}
        </button>
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error || 'Contact not found.'}
        </div>
      </div>
    );
  }

  const c = detail.contact;

  // Final disposition (not interested / not eligible / screen failed /
  // unreachable): the lead has left the funnel, so the header shows "No study
  // assigned" instead of a considered study. The history stays in the Profile
  // tab + timeline. This is a display rule — primary_study_id is left intact so
  // reactivating the lead restores its study.
  const finalDisposed = FINAL_DISPOSITIONS.includes(c.lifecycle_status || '');
  // Live prescreen status for the CURRENT study only (e.g. "Prescreening"),
  // resolved from the per-study eligibility roll-up. Shown beside the selector
  // so the single current-study label carries its status without the old
  // multi-study chip row.
  const primaryStudyStatus = (() => {
    const pid = detail.primary_study?.id;
    if (!pid) return null;
    const row = (detail.study_eligibility || []).find((se) => se.study_id === pid);
    return row ? attemptStatusMeta(row.attempt_status) : null;
  })();

  return (
    <div className="space-y-4">
      {/* Slim top row */}
      <button
        onClick={() => navigate(backTo)}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 transition-colors duration-150 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
      >
        <ChevronLeft className="h-4 w-4" /> {backLabel}
      </button>

      {/* Identity header */}
      <div className="relative rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{contactName(c)}</h2>
              <button
                onClick={() => setEditOpen(true)}
                aria-label="Edit contact details"
                title="Edit name, phone and email"
                className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700 dark:hover:text-primary-400 sm:min-h-0 sm:min-w-0 sm:p-1"
              >
                <Pencil className="h-4 w-4" />
              </button>
              {c.sms_opt_out && (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">
                  <Ban className="h-3 w-3" /> SMS opted out
                </span>
              )}
              {detail.rating_summary != null && detail.rating_summary.count >= 1 && detail.rating_summary.average !== null && (
                <span
                  title={`Average call rating across ${detail.rating_summary.count} rating${detail.rating_summary.count === 1 ? '' : 's'} — see the Profile tab`}
                  className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                >
                  ★{detail.rating_summary.average.toFixed(1)} · {detail.rating_summary.count}
                </span>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-400">
              <span className="inline-flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 text-gray-400" /> {formatPhone(c.phone_e164)}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5 text-gray-400" /> {c.email || '—'}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5 text-gray-400" /> Lead since {new Date(c.created_at).toLocaleDateString()}
              </span>
            </div>
            {/* Current study — ONE label under the name. While the lead is being
                considered this is the editable primary-study selector plus that
                study's live prescreen status ("Prescreening", etc.). Once the
                lead reaches a final disposition it reads "No study assigned" —
                the per-study history then lives in the Profile tab + the
                timeline's disposition-change line, not here. */}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {finalDisposed ? (
                <span
                  title="This lead reached a final disposition — reactivate it to assign a study. Study history is in the Profile tab."
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-500 dark:border-gray-600 dark:text-gray-400"
                >
                  <FlaskConical className="h-3.5 w-3.5" /> No study assigned
                </span>
              ) : (
                <>
                  <span className="text-xs font-medium uppercase tracking-wide text-gray-400">Considering</span>
                  <PrimaryStudySelect
                    contactId={c.id}
                    studyId={detail.primary_study?.id ?? null}
                    studyTitle={detail.primary_study ? studyInternalLabel(detail.primary_study) : null}
                    lifecycleStatus={c.lifecycle_status}
                    onChanged={(next) =>
                      setDetail((d) => (d ? {
                        ...d,
                        contact: { ...d.contact, primary_study_id: next?.id ?? null },
                        primary_study: next ? { id: next.id, slug: next.slug, title: next.title } : null,
                      } : d))
                    }
                    compact
                  />
                  {primaryStudyStatus && (
                    <span
                      title={`Prescreen status for ${detail.primary_study ? studyInternalLabel(detail.primary_study) : 'this study'}`}
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${primaryStudyStatus.badge}`}
                    >
                      {primaryStudyStatus.label}
                    </span>
                  )}
                </>
              )}
            </div>
            {/* Most recent entry reason — the latest landing page they completed
                to re-enter the system (history only, NOT the considered study). */}
            {cameInFor && (
              <p
                className="mt-1.5 inline-flex max-w-full items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500"
                title="The most recent landing page this lead completed"
              >
                <History className="h-3 w-3 shrink-0" />
                <span className="truncate">
                  Came in for {cameInFor.label}{cameInFor.source ? ` (via ${cameInFor.source})` : ''}
                </span>
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            <CallButton to={c.phone_e164} contactId={c.id} name={contactName(c)} />
            <button
              onClick={sendOptions}
              disabled={sendingOptions || !c.phone_e164 || c.sms_opt_out}
              aria-label="Text the response options"
              title={c.sms_opt_out ? 'Patient opted out of SMS'
                : !c.phone_e164 ? 'No phone number on file'
                : 'Text the 3 ways forward: 1 book a call · 2 fill the form · 3 not interested'}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-primary-600 px-3 py-1.5 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0"
            >
              {sendingOptions ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ListOrdered className="h-3.5 w-3.5" />}
              <span className="hidden sm:inline">{sendingOptions ? 'Sending…' : 'Send options'}</span>
            </button>
            <button
              onClick={() => setRefOpen(true)}
              aria-label="Open the study reference"
              title="Study reference: summary, recruiter script, stipend"
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
            >
              <BookOpen className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Study info</span>
            </button>
            <button
              onClick={toggleArchived}
              disabled={archBusy}
              aria-label={c.archived_at ? 'Unarchive conversation' : 'Archive conversation'}
              title={c.archived_at ? 'Unarchive this conversation' : 'Archive this conversation'}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
            >
              {archBusy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : c.archived_at ? (
                <ArchiveRestore className="h-3.5 w-3.5" />
              ) : (
                <Archive className="h-3.5 w-3.5" />
              )}
              <span className="hidden sm:inline">{c.archived_at ? 'Unarchive' : 'Archive'}</span>
            </button>
            {c.archived_at && (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                Archived
              </span>
            )}
            <LifecycleSelect
              contactId={c.id}
              value={c.lifecycle_status}
              scheduleReadiness={scheduleReadiness}
              primaryStudyId={primaryStudyId}
              onChanged={(next, studyId) =>
                setDetail((d) => {
                  if (!d) return d;
                  const contact = { ...d.contact, lifecycle_status: next };
                  // A study-verified status change may have re-pointed the
                  // considering study in the same PATCH — mirror it locally so
                  // the header chip doesn't go stale until the next refetch
                  // (PrimaryStudySelect resolves the label from its own list).
                  if (studyId && studyId !== d.contact.primary_study_id) {
                    contact.primary_study_id = studyId;
                    return { ...d, contact, primary_study: { id: studyId, slug: '', title: '' } };
                  }
                  return { ...d, contact };
                })
              }
            />
            <OwnerSelect
              contactId={c.id}
              value={c.owner_user_id}
              onChanged={(next) =>
                setDetail((d) => (d ? { ...d, contact: { ...d.contact, owner_user_id: next } } : d))
              }
            />
            {/* CTMS push (Expansion Phase H) — flows inline with the other
                action buttons so it never overlaps them. Renders nothing while
                CRM_CTMS_ENABLED is off. Keyed by contact: this page is NOT
                remounted on :id changes, so without the key an open dialog (and
                any in-flight push response) would survive contact navigation and
                write onto the wrong contact (the Phase F `key={running.id}`
                precedent). */}
            <PushToCtmsButton key={c.id} contactId={c.id} />
          </div>
        </div>
        {archError && (
          <p role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">{archError}</p>
        )}
        {optionsError && (
          <p role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">{optionsError}</p>
        )}
      </div>

      {msg && (
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-3 text-sm text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300">
          {msg}
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
        {/* Conversation — the screen recruiters live in. One fixed-height card:
            thread scrolls inside it, composer docked at the bottom. */}
        <div className="lg:col-span-3">
          {/* 240px ≈ app bar (3rem) + main padding + the contact header card +
              grid gap — keep in sync with the CRMLayout chrome so the thread
              fills the viewport without page scroll on desktop AND laptop. */}
          <div className="flex h-[65vh] min-h-[480px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 lg:h-[calc(100vh-240px)]">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5 dark:border-gray-700">
              <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                <MessageSquare className="h-4 w-4 text-primary-600 dark:text-primary-400" /> Conversation
              </h3>
              <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-400">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-400 opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-primary-500" />
                </span>
                Live
              </span>
            </div>
            <CommsThread contactId={c.id} reloadToken={reloadToken} usersById={usersById} onPing={onPing} contactName={contactName(c) === '(no name)' ? undefined : contactName(c)} contactPhone={c.phone_e164} />
            <ScheduledMessagesBar contactId={c.id} reloadToken={schedToken + pingTick} />
            <Composer
              key={c.id}
              contactId={c.id}
              studyId={detail.primary_study?.id ?? null}
              contactName={contactName(c) === '(no name)' ? undefined : contactName(c)}
              smsOptOut={c.sms_opt_out}
              hasPhone={!!c.phone_e164}
              hasEmail={!!c.email}
              onSent={() => setReloadToken((t) => t + 1)}
              onScheduled={() => setSchedToken((t) => t + 1)}
              prefill={composerDraft}
              onPrefillConsumed={() => setComposerDraft(null)}
            />
          </div>
        </div>

        {/* Tabbed work rail: next steps / screening / profile. Grows to its full
            content height and scrolls with the whole page (no inner scroll box)
            so the profile / next-steps / prescreening cards sit on the page
            instead of being crammed into a short window. */}
        <div className="lg:col-span-2">
          <ContactRail
            detail={detail}
            enrollments={enrollments}
            reloadToken={reloadToken}
            pingTick={pingTick}
            onSent={() => setReloadToken((t) => t + 1)}
            onChanged={() => { setReloadToken((t) => t + 1); notifyStatsChanged(); }}
            isSuperAdmin={isSuperAdmin}
            reenrolling={reenrolling}
            onReEnroll={reEnroll}
            hasRunnableStudy={(detail.leads || []).some((l) => Boolean(l.study_slug))}
            prefillComposer={(text) => setComposerDraft({ text, mode: 'sms' })}
            scheduleReadiness={scheduleReadiness}
            onToggleGate={onToggleGate}
          />
        </div>
      </div>

      <StudyReferenceDrawer
        open={refOpen}
        onClose={() => setRefOpen(false)}
        contactStudySlugs={leadStudySlugs}
        initialStudyId={detail.primary_study?.id ?? null}
      />

      {editOpen && (
        <EditContactModal
          contact={c}
          onClose={() => setEditOpen(false)}
          onSaved={(updated: Contact) => {
            setEditOpen(false);
            if (updated) {
              setDetail((d) => (d ? { ...d, contact: { ...d.contact, ...updated } } : d));
            }
          }}
        />
      )}
    </div>
  );
}
